/* eslint-disable react/jsx-props-no-spreading */
import React, {PureComponent} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import {v4 as uuidv4} from 'uuid';
/* Components */
import DashboardCard from './dashboard-card/dashboard-card';
import DashboardBlock from './dashboard-block/dashboard-block';
import CloneSettingsModal from '../settings-modal/clone-settings-modal';
// Actions
import {
  addDashboardToFavorite,
  removeDashboardFromFavorites,
  cloneDashboard,
  clearHighlightedDashboard,
  addDashboardForExport,
  removeDashboardForExport,
  exportDashboards,
} from '../../../store/dashboard/dashboard-actions';
// Helpers
import cloneDeep from 'lodash/cloneDeep';
import {getSortedTagList} from '../../../helpers/util';
// Styles
import './dashboard-item.scss';

const HIGHLIGHT_DURATION = 3000;

class DashboardItem extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isHighLighted: false,
      image: this.props.getImage(this.props.dashboard.modalFormData),
      showCloneDashboardModal: false,
      isCheckedForExport: false,
    };
  }

  componentDidMount() {
    this.setCheckedForExport();
    if (this.props.highlightedDashboard) {
      this.setHighLighted();
      setTimeout(() => {
        this.props.clearHighlightedDashboard();
      }, HIGHLIGHT_DURATION);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.highlightedDashboard !== this.props.highlightedDashboard) {
      this.setHighLighted();
    }
    if (!isEqual(prevProps.dashboard.modalFormData, this.props.dashboard.modalFormData)) {
      this.updateDashboardImage();
    }

    if (prevProps.dashboardsForExport !== this.props.dashboardsForExport) {
      this.setCheckedForExport();
    }
  }

  updateDashboardImage = () => {
    this.setState({
      image: this.props.getImage(this.props.dashboard.modalFormData),
    });
  };

  setHighLighted = () => {
    this.setState({
      isHighLighted: this.props.highlightedDashboard === this.props.dashboard.title,
    });
  };

  setCheckedForExport = () => {
    this.setState({
      isCheckedForExport: this.props.dashboardsForExport.some(
        (dashboardForExport) => dashboardForExport.id === this.props.dashboard.id
      ),
    });
  };

  renderDashboardTags = () => {
    const tagsInRow = getSortedTagList(this.props.dashboard.tags);
    if (!tagsInRow.length > 0) {
      return <div className="tags-row-section">-</div>;
    }
    return (
      <div className="tags-row">
        {tagsInRow.map((tag) => (
          <div
            className="glu-pill"
            // eslint-disable-next-line react/no-array-index-key
            key={tag.id}
            style={{backgroundColor: tag.bgColor ? tag.bgColor : ''}}
          >
            {tag.title}
          </div>
        ))}
      </div>
    );
  };

  cloneWidgetsAndLayoutsWithNewId = ({widgetsMap, layouts}) => {
    const newWidgetsMap = {};
    const newLayouts = {lg: []};
    Object.entries(widgetsMap).forEach(([widgetId, widgetData]) => {
      const newId = uuidv4();
      const currentLayout = layouts.lg.find((layout) => layout.i === widgetId);
      newLayouts.lg.push({
        ...currentLayout,
        i: newId,
      });
      newWidgetsMap[newId] = {
        ...widgetData,
        id: newId,
        layouts: {
          ...widgetData.layouts,
          lg: {
            ...widgetData.layouts.lg,
            i: newId,
          },
        },
      };
    });

    return {
      widgetsMap: newWidgetsMap,
      layouts: newLayouts,
    };
  };

  handleSaveClone = (values) => {
    const clonedDashboardData = cloneDeep(this.props.dashboard);
    clonedDashboardData.modalFormData.dashboardName = values.itemName;
    clonedDashboardData.modalFormData.selectedPermissionSettingsId = 1;
    clonedDashboardData.title = values.itemName;
    clonedDashboardData.description = values.dashboardDescription;
    clonedDashboardData.modalFormData.dashboardDescription = values.dashboardDescription;
    const {widgetsMap, layouts} = this.cloneWidgetsAndLayoutsWithNewId(clonedDashboardData);
    clonedDashboardData.widgetsMap = widgetsMap;
    clonedDashboardData.layouts = layouts;
    this.props.cloneDashboard(clonedDashboardData, this.props.queryParams, this.props.history);
  };

  toggleDashboardCloneModal = () => {
    this.setState({
      showCloneDashboardModal: !this.state.showCloneDashboardModal,
    });
  };

  openEditPage = () => {
    this.props.history.push(`/dashboard/edit/${this.props.dashboard.id}`);
  };

  onExportDashboard = () => {
    this.props.exportDashboards([this.props.dashboard]);
  };

  toggleCheckedForExport = () => {
    if (!this.state.isCheckedForExport) {
      this.setState({isCheckedForExport: true});
      this.props.addDashboardForExport(this.props.dashboard);
    } else {
      this.setState({isCheckedForExport: false});
      this.props.removeDashboardForExport(this.props.dashboard.id);
    }
  };

  render() {
    const dashboardCommonProps = {
      dashboard: this.props.dashboard,
      renderDashboardTags: this.renderDashboardTags,
      toggleDashboardModal: this.props.toggleDashboardModal,
      addDashboardToFavorite: this.props.addDashboardToFavorite,
      removeDashboardFromFavorites: this.props.removeDashboardFromFavorites,
      openEditPage: this.openEditPage,
      handleDashboardInfoClick: this.props.handleDashboardInfoClick,
      openDashboardPage: this.props.openDashboardPage,
      isHighLighted: this.state.isHighLighted,
      image: this.state.image,
      onClone: this.toggleDashboardCloneModal,
      queryParams: this.props.queryParams,
      onExportDashboard: this.onExportDashboard,
      toggleCheckedForExport: this.toggleCheckedForExport,
      isCheckedForExport: this.state.isCheckedForExport,
    };

    return (
      <>
        {this.props.layout === 'grid' ? (
          <DashboardCard {...dashboardCommonProps} />
        ) : (
          <DashboardBlock {...dashboardCommonProps} />
        )}
        {this.state.showCloneDashboardModal && (
          <CloneSettingsModal
            isOpen={this.state.showCloneDashboardModal}
            onClose={this.toggleDashboardCloneModal}
            onSave={this.handleSaveClone}
            cloneTarget="dashboard"
            dashboardDescription={this.props.dashboard.modalFormData.dashboardDescription}
          />
        )}
      </>
    );
  }
}

DashboardItem.propTypes = {
  dashboard: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleDashboardModal: PropTypes.func.isRequired,
  addDashboardToFavorite: PropTypes.func.isRequired,
  removeDashboardFromFavorites: PropTypes.func.isRequired,
  cloneDashboard: PropTypes.func.isRequired,
  handleDashboardInfoClick: PropTypes.func.isRequired,
  openDashboardPage: PropTypes.func.isRequired,
  getImage: PropTypes.func.isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  highlightedDashboard: PropTypes.string,
  clearHighlightedDashboard: PropTypes.func.isRequired,
  getDashboardCloneName: PropTypes.func.isRequired,
  layout: PropTypes.string.isRequired,
  queryParams: PropTypes.objectOf(PropTypes.any).isRequired,
  dashboardsForExport: PropTypes.array.isRequired,
};

DashboardItem.defaultProps = {
  highlightedDashboard: null,
};

const mapStateToProps = (state) => {
  return {
    highlightedDashboard: state.dashboardReducer.highlightedDashboard,
    queryParams: state.userReducer.queryParams,
    dashboardsForExport: state.dashboardReducer.dashboardsForExport,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    addDashboardToFavorite: (dashboard, queryParams) => () =>
      addDashboardToFavorite(dispatch, dashboard.id, queryParams),
    removeDashboardFromFavorites: (dashboard, queryParams) => () =>
      removeDashboardFromFavorites(dispatch, dashboard.id, queryParams),
    cloneDashboard: (dashboardData, queryParams, history) =>
      cloneDashboard(dispatch, dashboardData, queryParams, history),
    clearHighlightedDashboard: () => clearHighlightedDashboard(dispatch),
    addDashboardForExport: (dashboardData) => addDashboardForExport(dispatch, dashboardData),
    removeDashboardForExport: (dashboardId) => removeDashboardForExport(dispatch, dashboardId),
    exportDashboards: (dashboards) => exportDashboards(dispatch, dashboards),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardItem);
